import {
  company as fakerCompany,
  internet,
  lorem,
  name,
  phone,
  random,
} from "faker/locale/en_US";

import { defaultNoteStatuses } from "../../../root/defaultConfiguration";
import { contactGender } from "../../../contacts/contactModel";
import type { Company, Contact, ContactTouch } from "../../../types";
import type { Db } from "./types";
import { randomDate, weightedBoolean } from "./utils";

const maxContacts = {
  1: 1,
  10: 4,
  50: 12,
  250: 25,
  500: 50,
};

const TOUCH_TEMPLATES: Omit<ContactTouch, "date">[] = [
  { channel: "organic", source: "google", medium: "organic", landingPage: "/blog/b2b-messaging" },
  { channel: "organic", source: "google", medium: "organic", landingPage: "/services" },
  { channel: "paid", source: "google", medium: "cpc", campaign: "brand-search", landingPage: "/demo" },
  { channel: "paid", source: "linkedin", medium: "paid-social", campaign: "abm-q3", landingPage: "/case-studies" },
  { channel: "social", source: "linkedin", medium: "social", landingPage: "/blog/pipeline-math" },
  { channel: "email", source: "newsletter", medium: "email", campaign: "monthly", landingPage: "/webinar" },
  { channel: "direct", source: "direct", landingPage: "/" },
  { channel: "referral", source: "partner-site", medium: "referral", landingPage: "/services" },
];

const generateTouches = (firstSeen: string): ContactTouch[] => {
  const count = random.number({ min: 1, max: 5 });
  const start = new Date(firstSeen).getTime();
  const span = Math.min(Date.now() - start, 120 * 86400000);
  const dates = Array.from({ length: count }, () =>
    new Date(start + Math.random() * Math.max(span, 86400000)),
  ).sort((a, b) => a.getTime() - b.getTime());
  return dates.map((d) => ({
    ...random.arrayElement(TOUCH_TEMPLATES),
    date: d.toISOString(),
  }));
};

const getRandomContactDetailsType = () =>
  random.arrayElement(["Work", "Home", "Other"]) as "Work" | "Home" | "Other";

export const generateContacts = (db: Db, size = 500): Required<Contact>[] => {
  const nbAvailblePictures = 223;
  let numberOfContacts = 0;

  return Array.from(Array(size).keys()).map((id) => {
    const has_avatar =
      weightedBoolean(25) && numberOfContacts < nbAvailblePictures;
    const gender = random.arrayElement(contactGender).value;
    const first_name = name.firstName(gender as any);
    const last_name = name.lastName();
    const email_jsonb = [
      {
        email: internet.email(first_name, last_name),
        type: getRandomContactDetailsType(),
      },
    ];
    const phone_jsonb = [
      {
        number: phone.phoneNumber(),
        type: getRandomContactDetailsType(),
      },
      {
        number: phone.phoneNumber(),
        type: getRandomContactDetailsType(),
      },
    ];
    const avatar = {
      src: has_avatar
        ? "https://marmelab.com/posters/avatar-" +
          (223 - numberOfContacts) +
          ".jpeg"
        : undefined,
    };
    const title = fakerCompany.bsAdjective();

    if (has_avatar) {
      numberOfContacts++;
    }

    // choose company with people left to know
    let company: Company;
    do {
      company = random.arrayElement(db.companies);
    } while ((company.nb_contacts ?? 0) >= maxContacts[company.size]);
    company.nb_contacts = (company.nb_contacts ?? 0) + 1;

    const first_seen = randomDate(new Date(company.created_at)).toISOString();
    const last_seen = first_seen;

    return {
      id,
      first_name,
      last_name,
      gender,
      title: title.charAt(0).toUpperCase() + title.substr(1),
      company_id: company.id,
      company_name: company.name,
      email_jsonb,
      phone_jsonb,
      background: lorem.sentence(),
      acquisition: random.arrayElement(["inbound", "outbound"]),
      touches: generateTouches(first_seen),
      avatar,
      first_seen: first_seen,
      last_seen: last_seen,
      has_newsletter: weightedBoolean(30),
      status: random.arrayElement(defaultNoteStatuses).value,
      // Base funnel position; finalize() promotes deal contacts to
      // opportunity/customer so the funnel is coherent with the pipeline.
      lifecycle_stage: random.arrayElement([
        "subscriber",
        "lead",
        "lead",
        "lead",
        "mql",
        "mql",
        "sql",
      ]),
      tags: random
        .arrayElements(db.tags, random.arrayElement([0, 0, 0, 1, 1, 2]))
        .map((tag) => tag.id), // finalize
      sales_id: company.sales_id!,
      nb_tasks: 0,
      linkedin_url: null,
      // Live-workspace columns (round 43): the demo computes its score
      // client-side, so the persisted value stays 0 here.
      score: 0,
      score_manual: 0,
      email_opt_out: false,
      source: null,
      custom_fields: {},
    };
  });
};
